import { KeyValue, NgForOf, NgIf }                                                              from '@angular/common';
import { AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule }                                  from '@angular/forms';
import { ErrorStateMatcher }                                                        from '@angular/material/core';
import { MatFormFieldControl }                                                      from '@angular/material/form-field';
import { MatInputModule }                                                           from '@angular/material/input';
import { MatProgressBarModule }                                                     from '@angular/material/progress-bar';
import { MatSelect, MatSelectModule }                                               from '@angular/material/select';
import { UntilDestroy, untilDestroyed }                                             from '@ngneat/until-destroy';
import { PushPipe }                                                                 from '@ngrx/component';
import { arrayIsNotNull, includes, stringIsNotNull }                                from '@shared/helper/LDHelper';
import { NgxMatSelectSearchModule }                                                 from 'ngx-mat-select-search';
import { BehaviorSubject, forkJoin, of, ReplaySubject }                             from 'rxjs';
import { filter, switchMap, take, tap }                                             from 'rxjs/operators';
import { MyErrorStateMatcher }                                                      from 'src/app/components/main/steps/step-one/step-one.component';
import { AbstractControlViewAccessor }                                              from 'src/app/shared/utils/abstractControlViewAccessor';
import { Helper }                                                                   from '../../utils/helper';

@UntilDestroy ()
@Component ({
              selector:    'app-autocomplete-search',
              templateUrl: './autocomplete-search.component.html',
              styleUrls:   ['./autocomplete-search.component.css'],
              providers:   [
                {
                  provide: MatFormFieldControl,
                  useExisting: AutocompleteSearchComponent
                },
                {
                  provide:     NG_VALUE_ACCESSOR,
                  useExisting: forwardRef (() => AutocompleteSearchComponent),
                  multi:       true,
                },
              ],
              standalone:  true,
              imports:     [
                MatInputModule,
                MatSelectModule,
                NgxMatSelectSearchModule,
                NgForOf,
                PushPipe,
                ReactiveFormsModule,
                MatProgressBarModule,
                NgIf,
              ],
            })
export class AutocompleteSearchComponent extends AbstractControlViewAccessor implements OnInit, AfterViewInit {
  public matcher: ErrorStateMatcher;

  @Input () label: string;
  @Input () labelDisabled: string;
  @Input () noDatasLabel: string;
  @Input () placeholder: string;
  @Input () labelLoader: string;
  @Input () loading: boolean;
  @Input () disabled: boolean;
  @Input () data$: BehaviorSubject<KeyValue<string, any>[]>;

  // tslint:disable-next-line:no-output-on-prefix
  @Output () onSelection: EventEmitter<any>;

  public _mandatory: boolean;
  @Input ('mandatory')
  set mandatory (mdt: boolean) {
    this._mandatory = mdt;
  }

  @Input ('defaultValue')
  set defaultValue (v: KeyValue<string, string>) {
    this.matSelectControl.setValue (v);
  }

  public clearSearchInput: boolean                              = true;
  public searching: boolean                                     = false;
  public matSelectControl: FormControl                          = new FormControl ();
  public matSelectSearchControl: FormControl                    = new FormControl ();
  public filteredDatas$: ReplaySubject<KeyValue<string, any>[]> = new ReplaySubject<KeyValue<string, any>[]> (1);

  @ViewChild ('singleSelect', { static: false }) singleSelect: MatSelect;


  constructor () {
    super();
    this.onSelection = new EventEmitter<KeyValue<string, any>> ();
    this.matcher     = new MyErrorStateMatcher ();
  }

  id: string;
  empty: boolean;
  required: boolean;

  ngOnInit (): void {
    this.filterDatas ();
    this.initDatas ();
  }

  ngAfterViewInit () {
    this.setInitialValue ();
  }

  private initDatas () {
    this.data$.pipe (
      untilDestroyed (this),
      filter (v => {
        return arrayIsNotNull (v);
      }),
      tap ((vs: KeyValue<string, string>[]) => {
        this.filteredDatas$.next (vs);
      }),
    ).subscribe ();
  }

  private filter (value: string) {
    of (value).pipe (
      untilDestroyed (this),
      switchMap ((value: string) => {
        return forkJoin ([of (value),of (this.data$.getValue ()),]);
      }),
    ).subscribe ({
                   next: ([value, kv]: [string, KeyValue<string, string>[]]) => {
                     const values = kv.filter ((option: KeyValue<string, any>) => {
                       if (!option.value) {
                         throw new Error (`no text properties in autocomplete datas in input...${JSON.stringify (option)}`);
                       }
                       const keyToSearch     = Helper.accentFold (value.toLowerCase ());
                       const elementToSearch = Helper.accentFold (option.value.toLocaleLowerCase ());
                       if (stringIsNotNull (keyToSearch)) {
                         return elementToSearch.indexOf(keyToSearch) !== -1;
                       }
                       return true;
                     });
                     this.filteredDatas$.next (values);
                   },
                 });
  }

  private filterDatas () {
    const _self = this;
    _self.matSelectSearchControl.valueChanges.pipe (
      untilDestroyed (this),
    ).subscribe (v => {
      _self.filter (v);
    });
  }

  protected setInitialValue () {
    const _self = this;
    _self.filteredDatas$
      .pipe (
        take (1),
        untilDestroyed (this),
      )
      .subscribe (() => {
        if (_self.singleSelect) {
          _self.singleSelect.compareWith = (a: KeyValue<string, any>, b: KeyValue<string, any>) => {
            return (
              a && b && a.value === b.value
            );
          };
        }
      });
  }

  handleValueChange (v: any) {
    this.writeValue (v);
    this.onChange (v);
    this.onTouch (v);
    this.onSelection.emit (v);
 }

  getLibelle (value: any): string {
    if (typeof value === 'object') {
      return value.libelle;
    } else {
      return value;
    }
  }

  protected updateDefaultValue (value: any) {
    this.defaultValue = value;
  }
}
